import React, { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  Card,
  Cifra,
  FilaCifras,
  SERIES,
  OTROS,
  COLOR,
  eur,
  eur0,
  pct,
  num,
  ejes,
  leyenda,
  linea,
  barra,
  useEndpoint,
} from '../ui';
import { BarrasH } from '../PortfolioCards';

/**
 * Agente de IA: cuánto acierta (aprobado tal cual), por qué se le rechaza y qué
 * cobros llegan después de que actúe.
 *
 * El acierto por tipo reutiliza el endpoint de rendimiento del agente
 * (`/ai-agent/rendimiento`), que es de toda la cartera y llega como mucho a 90
 * días: la card lo dice.
 */
const P = '/portfolio-performance';
const MAX_DIAS_IA = 90;

/**
 * `new_sms` y `new_whatsapp` fuera del total: con cartera nueva meten cientos de
 * revisiones al día que se aprueban solas, y el % sube sin que el agente acierte
 * más (memoria `medir-revisiones-ia-por-periodo`).
 */
const FUERA_DEL_TOTAL = ['new_sms', 'new_whatsapp'];
const TIPOS_EN_GRAFICO = 5;

const TIPO = {
  reply_whatsapp: 'Responder WhatsApp',
  reply_email: 'Responder email',
  reply_sms: 'Responder SMS',
  follow_up_whatsapp: 'Seguimiento WhatsApp',
  follow_up_email: 'Seguimiento email',
  follow_up_call: 'Seguimiento llamada',
  payment_verification: 'Verificar pago',
  new_whatsapp: 'Primer WhatsApp',
  new_sms: 'Primer SMS',
};
const nombreTipo = t => TIPO[t] || t;

/** El lunes de la semana de un día 'YYYY-MM-DD'. */
function semanaDe(dia) {
  const d = new Date(`${dia}T12:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}
const etiquetaSemana = s => new Date(`${s}T12:00:00Z`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });

export default function IASection({ params, period }) {
  const dias = Math.min(MAX_DIAS_IA, period.dias);
  const rendimiento = useEndpoint('/ai-agent/rendimiento', { dias });
  const rechazos = useEndpoint(`${P}/ai-rejections`, params);
  const atribucion = useEndpoint(`${P}/payment-attribution`, params);

  const filas = useMemo(() => rendimiento.data?.filas || [], [rendimiento.data]);
  const total = useMemo(() => {
    const f = filas.filter(r => !FUERA_DEL_TOTAL.includes(r.task_type));
    const s = k => f.reduce((t, r) => t + (r[k] || 0), 0);
    return {
      propuestas: s('propuestas'),
      aprobadas: s('aprobadas'),
      rechazadas: s('rechazadas'),
      manuales: s('manuales'),
    };
  }, [filas]);
  // Decididas = aprobadas, rechazadas y «Lo hago yo». La revisión no permite editar
  // (`review.edited` nunca es true), así que aprobada ya es aprobada tal cual.
  const decididas = total.aprobadas + total.rechazadas + total.manuales;
  const agente = atribucion.data?.filas?.find(f => f.quien === 'agente');

  return (
    <div className='space-y-4'>
      <FilaCifras>
        <Cifra
          etiqueta={`Propuestas (${dias} días)`}
          cargando={rendimiento.estado === 'loading'}
          valor={rendimiento.estado === 'ok' ? num(total.propuestas) : '—'}
          pie='Sin primeros WhatsApp ni SMS'
        />
        <Cifra
          etiqueta='Aprobadas tal cual'
          cargando={rendimiento.estado === 'loading'}
          valor={decididas ? pct(Math.round((total.aprobadas / decididas) * 10000) / 100) : '—'}
          pie='De las que alguien revisó'
        />
        <Cifra
          etiqueta='Rechazadas o hechas a mano'
          cargando={rendimiento.estado === 'loading'}
          valor={decididas ? pct(Math.round(((total.rechazadas + total.manuales) / decididas) * 10000) / 100) : '—'}
          pie={decididas ? `${num(total.rechazadas)} rechazadas y ${num(total.manuales)} «Lo hago yo»` : null}
        />
        <Cifra
          etiqueta='Cobrado tras el agente'
          cargando={atribucion.estado === 'loading'}
          valor={agente ? eur0(agente.cobrado) : '—'}
          pie={agente ? `${pct(agente.pct_cobrado)} de lo cobrado en el periodo` : null}
        />
        <Cifra
          etiqueta='Motivos de rechazo'
          cargando={rechazos.estado === 'loading'}
          valor={rechazos.data ? num(rechazos.data.total) : '—'}
          pie={rechazos.data?.filas?.[0] ? `El primero: ${rechazos.data.filas[0].categoria.toLowerCase()}` : null}
        />
      </FilaCifras>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
        <AciertoPorSemana rendimiento={rendimiento} dias={dias} />
        <MotivosCard {...rechazos} />
        <AtribucionCard {...atribucion} />
      </div>
    </div>
  );
}

function AciertoPorSemana({ rendimiento, dias }) {
  const { chart, filasTabla } = useMemo(() => {
    const filas = rendimiento.data?.filas || [];
    const semanas = [...new Set(filas.map(f => semanaDe(f.dia)))].sort();
    const porTipo = new Map();
    for (const f of filas) {
      const k = `${f.task_type}|${semanaDe(f.dia)}`;
      const acc = porTipo.get(k) || { propuestas: 0, aprobadas: 0, rechazadas: 0, manuales: 0 };
      acc.propuestas += f.propuestas;
      acc.manuales += f.manuales || 0;
      acc.aprobadas += f.aprobadas;
      acc.rechazadas += f.rechazadas;
      porTipo.set(k, acc);
    }
    const volumen = new Map();
    for (const f of filas) volumen.set(f.task_type, (volumen.get(f.task_type) || 0) + f.propuestas);
    const tipos = [...volumen.entries()]
      .filter(([t]) => !FUERA_DEL_TOTAL.includes(t) && t !== '(sin tarea)')
      .sort((a, b) => b[1] - a[1])
      .slice(0, TIPOS_EN_GRAFICO)
      .map(([t]) => t);
    const acierto = (t, s) => {
      const a = porTipo.get(`${t}|${s}`);
      const dec = a ? a.aprobadas + a.rechazadas + a.manuales : 0;
      return dec ? Math.round((a.aprobadas / dec) * 10000) / 100 : null;
    };
    const datasets = tipos.map((t, i) =>
      linea(
        nombreTipo(t),
        semanas.map(s => acierto(t, s)),
        SERIES[i],
        { spanGaps: true }
      )
    );
    const tabla = [];
    for (const [k, a] of porTipo.entries()) {
      const [t, s] = k.split('|');
      const dec = a.aprobadas + a.rechazadas + a.manuales;
      tabla.push({
        semana: s,
        tipo: nombreTipo(t),
        propuestas: a.propuestas,
        aprobadas: a.aprobadas,
        rechazadas: a.rechazadas,
        manuales: a.manuales,
        acierto: dec ? (a.aprobadas / dec) * 100 : null,
      });
    }
    tabla.sort((x, y) => (x.semana === y.semana ? y.propuestas - x.propuestas : x.semana < y.semana ? 1 : -1));
    return { chart: { labels: semanas.map(etiquetaSemana), datasets }, filasTabla: tabla };
  }, [rendimiento.data]);

  return (
    <Card
      titulo='¿Acierta el agente a la primera?'
      lectura='Por semana y tipo de tarea, el % de propuestas que se aprueban tal cual. Una línea que sube es un tipo que aprende.'
      nota={`Acierto = aprobadas entre aprobadas, rechazadas y «Lo hago yo», de los últimos ${dias} días (el endpoint del agente llega a 90). Desde el 09/10/2026 casi no se rechaza: se pulsa «Lo hago yo», y cuenta igual como un no. Es de toda la cartera: no filtra por cliente.`}
      estado={rendimiento.estado}
      vacio={!filasTabla.length}
      filas={filasTabla}
      csv='acierto-del-agente'
      columnas={[
        { key: 'semana', label: 'Semana' },
        { key: 'tipo', label: 'Tipo de tarea' },
        { key: 'propuestas', label: 'Propuestas', fmt: num },
        { key: 'aprobadas', label: 'Aprobadas', fmt: num },
        { key: 'rechazadas', label: 'Rechazadas', fmt: num },
        { key: 'manuales', label: '«Lo hago yo»', fmt: num },
        { key: 'acierto', label: 'Acierto', fmt: v => pct(v == null ? null : Math.round(v * 100) / 100) },
      ]}
      ancho
      alto={300}
    >
      <Chart
        type='line'
        data={chart}
        options={{
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: leyenda,
            tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${pct(ctx.parsed.y)}` } },
          },
          scales: { ...ejes('% aprobadas tal cual'), y: { ...ejes('% aprobadas tal cual').y, max: 100 } },
        }}
      />
    </Card>
  );
}

function MotivosCard({ estado, data }) {
  const filas = data?.filas || [];
  return (
    <Card
      titulo='¿Por qué se rechazan sus propuestas?'
      lectura='Los rechazos del periodo, agrupados por lo que pedía quien revisó. La primera barra es lo que más conviene corregir en el prompt.'
      nota='Agrupados por palabras clave del motivo escrito. La tabla trae los tres motivos más repetidos de cada grupo, para comprobar que agrupa bien.'
      estado={estado}
      vacio={!filas.length}
      vacioTexto='No hubo rechazos en este periodo.'
      filas={filas.map(f => ({ ...f, ejemplos: f.ejemplos.join(' · ') }))}
      csv='motivos-de-rechazo'
      columnas={[
        { key: 'categoria', label: 'Motivo' },
        { key: 'rechazos', label: 'Rechazos', fmt: num },
        { key: 'pct', label: '%', fmt: pct },
        { key: 'tipo_principal', label: 'Tipo más afectado', fmt: v => (v ? nombreTipo(v) : '—') },
        { key: 'ejemplos', label: 'Ejemplos' },
      ]}
      alto={Math.max(240, filas.length * 26)}
    >
      <BarrasH
        titulo='Rechazos'
        etiquetas={filas.map(f => f.categoria)}
        valores={filas.map(f => f.rechazos)}
        fmt={num}
      />
    </Card>
  );
}

const QUIEN = {
  agente: 'Tras el agente',
  persona: 'Tras una persona',
  automatico: 'Solo automático',
  sin_gestion: 'Sin gestión en 7 días',
};

function AtribucionCard({ estado, data }) {
  const filas = (data?.filas || []).map(f => ({ ...f, quien: QUIEN[f.quien] || f.quien }));
  return (
    <Card
      titulo='¿Llegan más pagos después del agente o después de una persona?'
      lectura='Lo cobrado en el periodo según qué hubo en los 7 días anteriores a cada pago. No es causal: dice qué pasó antes, no por qué se pagó.'
      nota='Agente: una propuesta suya aprobada o automática. Persona: un WhatsApp manual o una llamada. Automático: correo, SMS o WhatsApp de secuencia. Por ese orden.'
      estado={estado}
      vacio={!data || !data.pagos}
      vacioTexto='No hubo cobros en este periodo.'
      filas={filas}
      csv='pagos-atribuidos'
      columnas={[
        { key: 'quien', label: 'Antes del pago' },
        { key: 'pagos', label: 'Pagos', fmt: num },
        { key: 'pct_pagos', label: '% de pagos', fmt: pct },
        { key: 'cobrado', label: 'Cobrado', fmt: eur },
        { key: 'pct_cobrado', label: '% de lo cobrado', fmt: pct },
      ]}
    >
      <Chart
        type='bar'
        data={{
          labels: filas.map(f => f.quien),
          datasets: [
            barra(
              'Cobrado',
              filas.map(f => f.cobrado),
              filas.map((f, i) => (i === 3 ? OTROS : [COLOR.s1, COLOR.s2, COLOR.s3][i]))
            ),
          ],
        }}
        options={{
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => `Cobrado: ${eur(ctx.parsed.y)}` } },
          },
          scales: ejes('€ cobrados'),
        }}
      />
    </Card>
  );
}
